import { uniqueId } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { defineMessages } from "react-intl";

import Button from "../Buttons/Button";
import { useFormatMessage } from "../Intl/Context";
import withContentHeight from "../Utils/withContentHeight";
import { useDeferredEffect } from "./useDeferredEffect";
import classNames from "../../../../node_modules/classnames/index";
import { assignForwardedRef } from "./assignForwardedRef";

const ExcerptableContainer = ({
  collapsedHeight = 200,
  collapsible = true,
  maskContainerRef,
  ...props
}) => {
  const { setContentHeightContainer, className, children } = props;
  const [displayAll, setDisplayAll] = useState(!collapsible);
  const [contentExceedsCollapsedHeight, setContentExceedsCollapsedHeight] =
    useState(false);

  const contentDOMIdRef = useRef(uniqueId("excerptable-container"));
  const contentDOMId = contentDOMIdRef.current;

  const t = useFormatMessage();
  const displayAllLabel = t(intlMessages.displayAll);
  const hideAllLabel = t(intlMessages.hideAll);

  const maxHeight = displayAll ? null : collapsedHeight;
  const overflow = displayAll ? null : "hidden";

  const [displayAllButton, setDisplayAllButton] = useState();
  const displayAllButtonHeight = displayAllButton?.offsetHeight || 0;

  const styles = useMemo(() => {
    return { maxHeight: maxHeight, overflow: overflow };
  }, [maxHeight, overflow]);

  const buttonLabel = displayAll ? hideAllLabel : displayAllLabel;

  // This allows for refocusing the displayAllLink button when the container is expanded
  // or collapsed, by using the value it had during the previous render.
  const wasDisplayed = useRef(displayAll);
  const shouldRefocusDisplayAll = wasDisplayed.current !== displayAll;
  wasDisplayed.current = displayAll;

  const toggleDisplayState = useCallback(() => {
    setDisplayAll(!displayAll);
  }, [displayAll]);

  const [excerptContainer, setExcerptContainer] = useState();
  const [maskContainer, setMaskContainer] = useState();

  useEffect(() => {
    assignForwardedRef(maskContainerRef, maskContainer);
  }, [maskContainerRef, maskContainer]);

  const processContentHeight = useCallback(
    contentHeight => {
      const contentOverflow = contentHeight <= collapsedHeight;

      setContentExceedsCollapsedHeight(contentOverflow);
    },
    [collapsedHeight]
  );

  const excerptContainerRef = useCallback(
    container => {
      setExcerptContainer(container);
      if (!container) return;

      setContentHeightContainer(container, processContentHeight);
    },
    [processContentHeight, setContentHeightContainer]
  );

  const displayAllLink = useMemo(() => {
    // Only process size when the this.excerptContainer has been rendered
    if (!excerptContainer || contentExceedsCollapsedHeight || !collapsible) {
      return null;
    }

    const button = (
      <Button
        type="button"
        ref={setDisplayAllButton}
        className="btn btn-primary-inverse btn-block-md excerpt-container-display-all"
        onClick={toggleDisplayState}
        label={buttonLabel}
        iconClassName={displayAll ? "icon-caret-top" : "icon-caret-bottom"}
        iconPosition="after"
        role="button"
        aria-expanded={displayAll ? "true" : "false"}
        aria-controls={contentDOMId}
        autoFocus={shouldRefocusDisplayAll}
      />
    );

    if (displayAll) {
      return button;
    } else {
      return <div className="excerpt-container-mask">{button}</div>;
    }
  }, [
    buttonLabel,
    collapsible,
    contentDOMId,
    contentExceedsCollapsedHeight,
    displayAll,
    excerptContainer,
    shouldRefocusDisplayAll,
    toggleDisplayState
  ]);

  // This effect hook ensures that all contents that's visually hidden, when the collapse
  // is in its collapsed state, are also unavailable for focusing and not shown by screen
  // readers.
  //
  // Since we use the actual content height to make some calculations, we use the css
  // `visiblity: hidden` rule and not `display: none` to ensure that the size of the
  // content is kept untouched.
  useEffect(() => {
    if (!excerptContainer || !maskContainer || !collapsible) return;

    const containerTop = maskContainer.getBoundingClientRect().top;
    const containerVisibleBottom = containerTop + maxHeight - displayAllButtonHeight;
    const childElements = excerptContainer.querySelectorAll("*");

    let firstHiddenElement;

    childElements.forEach(element => {
      const elementTop = element.getBoundingClientRect().top;

      // If we're in collapsed state, and the element's top is not in the currently
      // available viewbox, we ensure that the content is visibly hidden, and not shown
      // to screen readers. Otherwise, when the collapse is expanded, ensure that every
      // content is visible and focusable elements are not hidden.
      if (!displayAll && elementTop >= containerVisibleBottom) {
        element.style.visibility = "hidden";

        if (!firstHiddenElement) firstHiddenElement = element;
      } else {
        element.style.visibility = "visible";
      }
    });

    // Mark the first hidden element and make it programatically focusable, to allow us
    // to focusing it when the hidden content of the container is shown.
    if (
      firstHiddenElement &&
      !firstHiddenElement.getAttribute("data-first-hidden-element")
    ) {
      const previousFirstHiddenElement = excerptContainer.querySelector(
        "[data-first-hidden-element]"
      );
      // If there was another first-hidden-element set before a resize or something else,
      // reset its focusable attributes to avoid a duplicate [data-first-hidden-element]
      // in the DOM.
      if (previousFirstHiddenElement) {
        previousFirstHiddenElement.removeAttribute("data-first-hidden-element");
        previousFirstHiddenElement.removeAttribute("tabindex");
      }

      firstHiddenElement.setAttribute("tabindex", -1);
      firstHiddenElement.setAttribute("data-first-hidden-element", true);
    }
  });

  // Ensure that the first element that was hidden before the "display all" link was
  // clicked is focused. We use a deferred effect to ensure that the element's visibilty
  // was updated before we focus it.
  useDeferredEffect(() => {
    if (!displayAll || !excerptContainer) return;

    excerptContainer.querySelector("[data-first-hidden-element]")?.focus();
  }, [displayAll, excerptContainer]);

  const wrapperClassName = classNames(
    "d-flex",
    "flex-column",
    "excerpt-container",
    "h-100",
    props.wrapperClassName
  );

  return (
    <div className={wrapperClassName}>
      <div className={className} style={styles} ref={setMaskContainer}>
        {/* The following container allows us to calculate the actual content height */}
        <div
          id={contentDOMId}
          className="excerpt-container-inner"
          ref={excerptContainerRef}
          role="region"
        >
          {children}
        </div>
      </div>

      {displayAllLink}
    </div>
  );
};

export default withContentHeight(ExcerptableContainer);

const intlMessages = defineMessages({
  displayAll: {
    id: "excerptableContainer.displayAll",
    defaultMessage: "Lire la suite"
  },
  hideAll: {
    id: "excerptableContainer.hideAll",
    defaultMessage: "Réduire le texte"
  }
});
